<template>
  <fts-design-modal
    class="fts-flow-design-modal"
    ref="ftsDesignModal">
    <!-- title -->
    <template #title>
      <div class="flow-design-modal-title fts-flow-display">
        <!-- from state -->
        <fts-state 
          :state-id="formData.fromStateId"
          :states="states"
          class="flow-display-state"
          v-if="formData.fromStateId" />
        <fts-state
          :state="{ name: '(any state)' }"
          class="flow-display-state"
          v-else />
        <!-- direction -->
        <font-awesome-icon :icon="['fas', 'right-long']" class="flow-display-direction" />
        <!-- activity -->
        <fts-label-group
          :item-id="formData.activityId"
          :list="activities"
          emoji
          class="flow-display-activity"
          v-if="formData.activityId"
        />
        <div v-else class="flow-display-activity default">(no activity)</div>
        <!-- direction -->
        <font-awesome-icon :icon="['fas', 'right-long']" class="flow-display-direction" />
        <!-- to state -->
        <template v-if="formData.conditional">
          <template v-for="(state, stateIndex) in conditionalStates">
            <div :key="`or-${state.id}`" v-if="stateIndex" class="flow-display-alt">
              or
            </div>
            <fts-state
              :state-id="state.toStateId"
              :states="states"
              :key="`state-${state.id}`"
              class="flow-display-state"
              v-if="state.toStateId" />
          </template>
        </template>
        <template v-else>
          <fts-state 
            :state-id="formData.toStateId"
            :states="states"
            class="flow-display-state"
            v-if="formData.toStateId" />
          <fts-state
            :state="{ name: '(any state)' }"
            class="flow-display-state"
            v-else />
        </template>
      </div>
    </template>
    <!-- description -->
    <template #description>
      <span class="design-modal-header-tag">Flow</span>
      <span class="design-modal-header-description">
        determines a state flows another through specific activity
      </span>
    </template>
    <!-- body -->
    <template #body>
      <div class="fts-row gutters">
        <!-- description -->
        <div class="fts-col">
          <div class="fts-form-group">
            <fts-contenteditable
              v-model="formData.description"
              text-wrap
              placeholder="enter description (optional)"
            />
          </div>
        </div>
        <!-- error -->
        <div class="fts-col" v-if="error('fromStateId') || error('activityId')">
          <div class="design-modal-form-error flow-name-error">
            {{error('fromStateId') || error('activityId')}}
          </div>
        </div>
        <!-- from state -->
        <div class="fts-col fts-col-md-6" key="fdm-from-state-id">
          <fts-form-element
            :form-data="formData"
            :item="{
              fieldName: 'fromStateId',
              displayName: 'From State',
              icon: 'fts-icon-menu-caret-down',
            }"
            :validator="$v.formData"
            comfort-design
            disable-validator-error
          >
            <fts-select
              slot="formElement"
              slot-scope="{onInput, onBlur}"
              v-model="fromStateId"
              :options="states"
              :clearable="Boolean(formData.fromStateId)"
              comfort-design
              @input="onInput"
              @search:blur="onBlur"
            >
              <fts-state slot="selected-option-container" slot-scope="{option}" :state="option" />
              <fts-state slot="option" slot-scope="{option}" :state="option" />
              <div slot="no-options-footer" class="fts-select__no-options-footer">
                <fts-btn
                  text
                  color="primary"
                  text-transform-init
                  full-width
                  @click="createState('from')"
                >
                  <font-awesome-icon :icon="['fas', 'plus']" />
                  Add New
                </fts-btn>
              </div>
            </fts-select>
          </fts-form-element>
        </div>
        <!-- activity -->
        <div class="fts-col fts-col-md-6" key="fdm-activity">
          <fts-form-element
            :form-data="formData"
            :item="{
              fieldName: 'activityId',
              displayName: 'Activity',
              icon: 'fts-icon-menu-caret-down',
            }"
            :validator="$v.formData"
            comfort-design
            disable-validator-error
          >
            <fts-select
              slot="formElement"
              slot-scope="{ formData, item, onInput, onBlur }"
              :form-data="formData"
              :item="item"
              :options="activities"
              comfort-design
              @input="onInput"
              @search:blur="onBlur"
            >
              <fts-label-group
                slot="selected-option-container"
                slot-scope="{ option }"
                :item-id="option.id"
                :list="activities"
                emoji
              />
              <fts-label-group
                slot="option"
                slot-scope="{ option }"
                :item-id="option.id"
                :list="activities"
                emoji
              />
            </fts-select>
          </fts-form-element>
        </div>
        <!-- to state -->
        <div class="fts-col fts-col-md-6" v-if="!formData.conditional" key="fdm-to-state-id">
          <fts-form-element
            :form-data="formData"
            :item="{
              'fieldName': 'toStateId',
              'displayName': 'To State',
              'icon': 'fts-icon-menu-caret-down',
            }"
            comfort-design
          >
            <fts-select
              slot="formElement"
              v-model="toStateId"
              :options="states"
              :clearable="Boolean(formData.toStateId)"
              comfort-design
            >
              <fts-state slot="selected-option-container" slot-scope="{option}" :state="option" />
              <fts-state slot="option" slot-scope="{option}" :state="option" />
              <div slot="no-options-footer" class="fts-select__no-options-footer">
                <fts-btn
                  text
                  color="primary"
                  text-transform-init
                  full-width
                  @click="createState('to')"
                >
                  <font-awesome-icon :icon="['fas', 'plus']" />
                  Add New
                </fts-btn>
              </div>
            </fts-select>
          </fts-form-element>
        </div>
        <!-- conditional -->
        <div class="fts-col">
          <fts-form-element
            :form-data="formData"
            :item="{
              fieldName: 'conditional',
              input: 'toggleSwitch',
            }"
            comfort-design
            @input="toggleCondition"
          />
        </div>
        <!-- conditional states -->
        <draggable 
          class="fts-col design-modal-form-group" 
          v-model="formData.conditionalStates" 
          v-bind="dragOptions" 
          draggable=".conditional-state" 
          handle=".conditional-state-handle"
          v-if="formData.conditional">
          <!-- conditional state -->
          <div class="conditional-state" v-for="(condition, conditionIndex) in formData.conditionalStates" :key="condition.id">
            <div class="fts-row gutters">
              <div class="conditional-state-select-wrapper fts-col fts-col-md-6">
                <div class="conditional-state-index-wrapper">
                  <font-awesome-icon :icon="['fas', 'grip-vertical']" fixed-width class="conditional-state-handle" />
                  <div class="conditional-state-index">{{conditionIndex + 1}}.</div>
                </div>
                <fts-select 
                  class="fts-col"
                  :value="Boolean(condition.toStateId) ? condition.toStateId : ''"
                  :options="states"
                  :clearable="Boolean(condition.toStateId)"
                  comfort-design
                  placeholder="any state"
                  @option:selected="updateConditionalState($event, condition)"
                  @option:clear="updateConditionalState(null, condition)"
                >
                  <fts-state slot="selected-option-container" slot-scope="{option}" :state="option" />
                  <fts-state slot="option" slot-scope="{option}" :state="option" />
                  <div slot="no-options-footer" class="fts-select__no-options-footer">
                    <fts-btn
                      text
                      color="primary"
                      text-transform-init
                      full-width
                      @click="createState('to', condition)"
                    >
                      <font-awesome-icon :icon="['fas', 'plus']" />
                      Add New
                    </fts-btn>
                  </div>
                </fts-select>

                <fts-btn text icon class="conditional-state-remove" @click="removeCondition(condition)">
                  <font-awesome-icon :icon="['far', 'trash-alt']" />
                </fts-btn>
              </div>
              <!-- condition -->
              <div class="conditional-state-condition-wrapper fts-col">
                <fts-collapsible-element 
                  :condition="Boolean(condition.condition)"
                  label="condition" 
                  immersive
                >
                  <fts-formula
                    slot="content"
                    v-model="condition.condition"
                    :variables="variables"
                    autofocus
                  />
                </fts-collapsible-element>
              </div>
              <!-- description -->
              <div class="conditional-state-description-wrapper fts-col">
                <fts-collapsible-element
                  :condition="Boolean(condition.description)"
                  label="description"
                  immersive
                >
                  <fts-contenteditable
                    slot="content"
                    v-model="condition.description"
                    text-wrap
                    autofocus
                    placeholder="enter description (optional)"
                    class="conditional-state-description"
                  />
                </fts-collapsible-element>
              </div>
            </div>
          </div>
          <!-- header -->
          <div slot="header" class="conditional__header">
            <div class="design-modal-form-label">Conditional States</div>
            <div class="design-modal-form-error" v-if="error('conditionalStates')">
              {{error('conditionalStates')}}
            </div>
          </div>
          <!-- footer -->
          <div slot="footer" class="conditional__footer">
            <fts-btn
              sm
              outline
              color="primary"
              text-transform-init
              @click="addCondition"
            >
              <font-awesome-icon :icon="['fas', 'plus']" /> Add Conditional State
            </fts-btn>
          </div>
        </draggable>
        <!-- error -->
        <div class="fts-col" v-if="error('conditionalStates') && !formData.conditional">
          <div class="design-modal-form-error flow-conditional-error">
            <div>Oops, one conflict was found.</div>
            <div class="flow-conditional-error-actions">
              <fts-btn outline sm color="primary" text-transform-init @click="continueConditionStates">
                Continue with conditional states
              </fts-btn>

              <fts-btn outline sm color="danger" text-transform-init @click="removeConditionalStates">
                Remove conditional states
              </fts-btn>
            </div>
          </div>
        </div>
      </div>
      <!-- state design modal -->
      <fts-state-design-modal ref="ftsStateDesignModal" />
    </template>
    <!-- footer -->
    <template #footerLeft>
      <fts-btn text icon @click="remove" v-if="isExist">
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
    </template>
    <template #footerRight>
      <fts-btn text @click="beforeClose">Cancel</fts-btn>

      <fts-btn color="primary" text @click="done(true)" v-if="Boolean(saveCallBack)">
        <font-awesome-icon :icon="['fas', 'check']" /> Save
      </fts-btn>

      <fts-btn outline color="primary" @click="done">
        <font-awesome-icon :icon="['fas', 'check']" /> Ok
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import draggable from "vuedraggable";
import changesets from "diff-json";

import commons from "../commons/commons";
import validator from "../commons/designValidator";
import canvasDesign from "../commons/canvasDesign";

import FTSDesignModal from "./FTSDesignModal";
import FTSBtn from "../elements/FTSBtn";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSFormElement from "../elements/FTSFormElement";
import FTSSelect from "../elements/FTSSelect";
import FTSState from "../elements/FTSState";
import FTSLabelGroup from "../elements/FTSLabelGroup";
import FTSFormula from "../design/FTSFormula.vue";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSStateDesignModal from "./FTSStateDesignModal";

export default {
  components: {
    draggable,
    "fts-design-modal": FTSDesignModal,
    "fts-btn": FTSBtn,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-form-element": FTSFormElement,
    "fts-select": FTSSelect,
    "fts-state": FTSState,
    "fts-label-group": FTSLabelGroup,
    "fts-formula": FTSFormula,
    "fts-contenteditable": FTSContenteditable,
    "fts-state-design-modal": FTSStateDesignModal,
  },
  data: () => ({
    formData: null,
    originalData: null,
    resolve: null,
    saveCallBack: null,
    dragOptions: commons.dragOptions,
  }),
  validations() {
    let formData = {};
    formData = validator.flowDesign(this.flows, this.formData);
    return { formData };
  },
  methods: {
    async show(item, saveCallBack) {
      this.saveCallBack = saveCallBack;
      this.formData = _.cloneDeep(item);

      return new Promise(resolve => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData)
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });             
            } else {
              this.reset();
            }
          },
        });
     
        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
        });
     })
    },
    done(save) {
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        if (save) {
          this.saveCallBack && this.saveCallBack(this.formData);
          this.originalData = _.cloneDeep(this.formData);
        } else {
          this.resolve && this.resolve(this.formData);
          this.reset();
        }
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    async remove() {
      const res = await this.$confirmation.show({
        type: 'delete',
        title: 'Flow'
      });

      if (res === true) {
        this.resolve && this.resolve(true);
        this.reset();
      }
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
    },
    error(key) {
      return validator.flowDesignError(this.$v.formData, key); 
    },
    addCondition(item) {
      if (!this.formData.conditionalStates)
        this.$set(this.formData, 'conditionalStates', []);

      this.formData.conditionalStates.push({
        ...item, 
        id: commons.newUUID()
      });
    },
    removeCondition(item) {
      let conditions = this.formData.conditionalStates;
      conditions.splice(conditions.indexOf(item), 1);
    },
    removeConditionalStates() {
      this.$delete(this.formData, "conditionalStates");
    },
    continueConditionStates() {
      this.$set(this.formData, "conditional", true);
    },
    updateConditionalState(item, state) {
      this.$set(state, "toStateId", Boolean(item && item.id) ? item.id : null);
    },
    toggleCondition() {
      if (this.formData.conditional) {
        if (this.formData.toStateId) {
          this.addCondition({ toStateId: this.formData.toStateId });
          this.toStateId = null;
        } else {
          this.addCondition();
        }
      } else if (this.formData.conditionalStates) {
        if (this.formData.conditionalStates[0] && this.formData.conditionalStates[0].toStateId) {
          this.toStateId = this.formData.conditionalStates[0].toStateId;
        }

        this.removeConditionalStates();
      }
    },
    async createState(type, item) {
      const result = await this.$refs.ftsStateDesignModal.show(
        { ...canvasDesign.initState(item) },
        null,
        null,
        null,
        { disableAdvanced: true }
      );

      if (result != null && result != true) {
        let canvas = _.cloneDeep(this.$store.state.canvas);
        canvasDesign.setState(canvas, result);
        this.$store.dispatch("updateVectorCanvas", canvas);
        if (type === 'from') {
          this.$set(this.formData, "fromStateId", result.id);
        } else if (type === 'to' && this.formData.conditional) {
          if (item && item.id) {
            const findIndex = this.formData.conditionalStates.findIndex(x => x.id == item.id);
            if (findIndex > -1) {
              this.$set(this.formData.conditionalStates, findIndex, {
                ...item,
                toStateId: result.id,
              });
            }
          }
        } else if (type === 'to') {
          this.$set(this.formData, "toStateId", result.id);
        }
      }
    }
  },
  computed: {
    variables() {
      return [
        {
          name: "Entry",
          fieldName: "entry",
          type: 35,
          moduleId: this.$store.state.vectorId.toString(),
        }
      ];
    },
    flows() {
      return this.$store.getters.canvasFlows;
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    states() {
      return this.$store.getters.canvasStates.concat({
        id: '',
        name: "(any state)",
      }).filter(x => x.name)
    },
    activities() {
      return this.$store.getters.canvasActivities;
    },
    isExist() {
      return this.flows.some(x => x.id === this.formData.id);
    },
    conditionalStates() {
      return (
        this.formData && 
        this.formData.conditionalStates && 
        this.formData.conditionalStates.filter(x => x.toStateId)
      );
    },
    fromStateId: {
      get() {
        return Boolean(this.formData && this.formData.fromStateId) ? this.formData.fromStateId : "";
      },
      set(val) {
        this.$set(this.formData, "fromStateId", Boolean(val) ? val : null);
      }
    },
    toStateId: {
      get() {
        return Boolean(this.formData && this.formData.toStateId) ? this.formData.toStateId : "";
      },
      set(val) {
        this.$set(this.formData, "toStateId", Boolean(val) ? val : null);
      }
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-flow-design-modal {
  .flow-design-modal-title {
    margin-bottom: .275rem;
    .flow-display-state {
      margin-top: .135rem;
      margin-bottom: .135rem;
      & > div {
        font-size: 1rem;
      }
    }
    .flow-display-activity {
      font-size: 1rem;
    }
  }
  .flow-name-error {
    @include theme() {
      background: rgba($color: theme-get("fts-danger"), $alpha: 0.07);
    }
    margin-bottom: .75rem; 
    padding: .875rem;
    border-radius: $comfort-border-radius;
  }
  .flow-conditional-error {
    @include theme() {
      background: rgba($color: theme-get("fts-danger"), $alpha: 0.07);
    }
    padding: .875rem;
    border-radius: $comfort-border-radius;
    .flow-conditional-error-actions {
      @include flex-gap($gap: 0.375rem);
      justify-content: flex-end;
      margin-top: .375rem;
    }
  }
  .conditional-state {
    margin-bottom: 1.15rem;
    border-bottom: 1px solid var(--fts-gray-200);
    &:hover {
      .conditional-state-index-wrapper {
        .conditional-state-index {
          display: none;
        }
        .conditional-state-handle {
          display: inline-block;
        }
      }
    }
    .conditional-state-select-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-left: 1rem;
      margin-bottom: 1rem;
    }
    .conditional-state-index-wrapper {    
      position: absolute;
      transform: translateX(-16px);
    }
    .conditional-state-index {
      font-weight: 500;
      color: var(--fts-form-label);
    }
    .conditional-state-handle {
      font-size: .75rem;
      color: var(--fts-default);
      cursor: grab;
      display: none;
    }
    .conditional-state-remove {
      margin-left: .375rem;
    }
    .conditional-state-condition-wrapper {
      margin-left: 1rem;
      margin-bottom: 1rem;
    }
    .conditional-state-description-wrapper {
      margin-left: 1rem;
      margin-bottom: 1rem;
      .conditional-state-description {
        color: var(--fts-form-helper);
        &::before {
          -webkit-text-fill-color: var(--fts-form-control-placeholder);
          font-weight: 300;
          opacity: 1;
        }
      }
    }
  } 
  .conditional__header {
    margin-bottom: .75rem;
  }
}
</style>